// Expanded card: single-screen item form (formerly a 3-step wizard).
// "Product" = what the restaurant currently buys (family + variant picker,
// with inline catalog creation); picking a variant collapses the picker into
// a one-line summary strip ("Change" re-opens it). Below, "Flexibility" =
// what they're willing to change (per-attribute open-to-switch flags including
// pack format, each with a catalog-driven picker of the alternatives they'd
// accept — none picked = open to any — plus notes; see flexibility-fields.tsx)
// and "Demand" = volume and prices; both stay faded and locked until a variant
// is chosen. Create POSTs one item; edit PATCHes only the changed fields. A
// failed save keeps the form open with values intact.

import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  Image,
  Input,
  Spinner,
  Text,
  useToast,
} from '@elkaso-app/web-design';
import { useGetMasterProductApi, useGetMasterProductsApi } from 'apis/master-products';
import {
  RestaurantInquiryItem,
  useAddRestaurantInquiryItemsApi,
  useUpdateRestaurantInquiryItemApi,
} from 'apis/restaurant-inquiries';
import { describeUnits } from 'pages/inquiry-processor/utils/describe-units';
import { apiErrorMessage } from 'pages/inquiry-processor/utils/inquiry-actions';
import { AttrSelections, attrValue, ELIMINATION_ATTRS } from 'pages/inquiry-processor/utils/variant-elimination';
import { useEffect, useMemo, useState } from 'react';
import { FiImage } from 'react-icons/fi';
import { FlexibilityFields } from './flexibility-fields';
import { InlineFamilyForm } from './inline-family-form';
import { InlineVariantForm } from './inline-variant-form';
import { VariantEliminationFields, VariantEliminationPatch } from './variant-elimination-fields';
import {
  draftFromItem,
  draftToCreateItem,
  draftToUpdatePayload,
  draftValid,
  makeDraft,
  numOnly,
  WizardDraft,
} from './wizard-draft';

interface ItemWizardProps {
  inquiryId: number | string;
  // Master products already on the inquiry (the other items') — excluded from
  // step 1's variant elimination so an item can't duplicate another item's
  // variant. Other variants of the same family stay pickable.
  excludeIds: number[];
  item?: RestaurantInquiryItem;
  onClose: () => void;
  // Fires after a successful CREATE (not edit), just before onClose — the
  // board shows a skeleton card in the grid while the invalidated inquiry
  // query refetches, so the new item doesn't briefly "disappear".
  onSaved?: () => void;
  // Drops the card frame (border + padding) — for hosts that provide their
  // own chrome, like the Edit Item modal.
  embedded?: boolean;
}

const SectionTitle = ({ label, hint }: { label: string; hint?: string }) => (
  <Flex align='baseline' gap='2' mb='2'>
    <Text fontSize='xs' fontWeight='bold' color='gray.500' textTransform='uppercase' letterSpacing='wider'>
      {label}
    </Text>
    {hint && (
      <Text fontSize='sm' color='gray.600'>
        {hint}
      </Text>
    )}
  </Flex>
);

export const ItemWizard = ({ inquiryId, excludeIds, item, onClose, onSaved, embedded }: ItemWizardProps) => {
  const toast = useToast();
  const addItems = useAddRestaurantInquiryItemsApi();
  const updateItem = useUpdateRestaurantInquiryItemApi();

  const [draft, setDraft] = useState<WizardDraft>(makeDraft);
  const [inlineForm, setInlineForm] = useState<'family' | 'variant' | null>(null);
  const [typedFamilyName, setTypedFamilyName] = useState('');
  // The variant picker is the big element; once a variant is resolved it
  // collapses into a summary strip. Create starts open, edit starts collapsed
  // (the item already has a variant) — "Change" on the strip re-opens it.
  const [pickerOpen, setPickerOpen] = useState(!item);
  // A variant created inline may not match the picker's active filters (e.g.
  // a brand nothing was filtered to yet) — tracked so the picker can clear
  // them and surface the variant it was just told to select.
  const [justCreatedVariantId, setJustCreatedVariantId] = useState<number>();

  // Edit mode: the item's nested product summary lacks familyId, so fetch the
  // full product once and seed the draft from item + product.
  const { data: editProduct, isError: editProductError } = useGetMasterProductApi(item?.masterProductId, {
    enabled: !!item,
  });
  const [seeded, setSeeded] = useState(false);
  useEffect(() => {
    if (!item || !editProduct || seeded) return;
    setDraft(draftFromItem(item, editProduct));
    setSeeded(true);
  }, [item, editProduct, seeded]);

  // Family variants for the step-3 alternatives multi-select (cached per
  // familyId, so this shares step 1's request). Catalog-driven by design:
  // only values that exist on ACTIVE variants are offered as alternatives.
  const { data: familyVariants } = useGetMasterProductsApi(draft.familyId);
  const activeVariants = useMemo(() => (familyVariants?.data ?? []).filter((p) => p.isActive), [familyVariants]);

  // The resolved variant, for the summary strip's photo. Unfiltered on
  // purpose (edit mode may hold an inactive variant); falls back to the edit
  // product while the variants query is in flight.
  const selectedVariant = useMemo(
    () =>
      (familyVariants?.data ?? []).find((p) => p.id === draft.masterProductId) ??
      (editProduct && editProduct.id === draft.masterProductId ? editProduct : undefined),
    [familyVariants, editProduct, draft.masterProductId]
  );

  // Flexibility + Demand are locked (faded, non-interactive) until a variant
  // is resolved — their contents derive from the chosen variant.
  const locked = !draft.masterProductId;
  const showPicker = pickerOpen || locked;

  // Strip text: the distinguishing attributes + a units sentence, both from
  // the draft picks (available even when the resolved variant isn't cached).
  const stripAttrs = [draft.sel.brand, draft.sel.origin, draft.sel.state, draft.sel.spec].filter(Boolean).join(' · ');
  const stripUnits = describeUnits({
    baseUnit: draft.sel.baseUnit || undefined,
    itemUnit: draft.sel.itemUnit || undefined,
    netContent: draft.sel.netContent ? Number(draft.sel.netContent) : null,
    packUnit: draft.sel.packUnit || undefined,
    packSize: draft.sel.packSize ? Number(draft.sel.packSize) : null,
  });

  const patchDraft = (patch: Partial<WizardDraft>) => setDraft((d) => ({ ...d, ...patch }));

  const onEliminationPatch = (patch: VariantEliminationPatch) => {
    // The patch always carries explicit keys; masterProductId: undefined must
    // CLEAR the field, so spread objects, not conditionals.
    setDraft((d) => ({
      ...d,
      ...('familyId' in patch ? { familyId: patch.familyId, alt: {} } : {}),
      ...('familyName' in patch ? { familyName: patch.familyName ?? '' } : {}),
      ...(patch.sel ? { sel: patch.sel } : {}),
      ...('masterProductId' in patch ? { masterProductId: patch.masterProductId } : {}),
    }));
    // Picking a row resolves the product — collapse the picker to its summary
    // strip. Deselecting (undefined) keeps it open for another pick.
    if (typeof patch.masterProductId === 'number') setPickerOpen(false);
  };

  const save = async () => {
    try {
      if (item) {
        const data = draftToUpdatePayload(draft, item);
        if (Object.keys(data).length) await updateItem.mutateAsync({ id: inquiryId, itemId: item.id, data });
      } else {
        await addItems.mutateAsync({ id: inquiryId, items: [draftToCreateItem(draft)] });
      }
    } catch (err) {
      // Keep the wizard open with values intact so ops can retry.
      toast({
        title: item ? 'Failed to update item' : 'Failed to add item',
        description: apiErrorMessage(err),
        status: 'error',
      });
      return;
    }
    toast({ title: item ? 'Item updated' : 'Item added', status: 'success' });
    if (!item) onSaved?.();
    onClose();
  };

  const isSaving = addItems.isLoading || updateItem.isLoading;

  // Edit mode holds a spinner until the draft is seeded — a blank "add" form
  // that gets overwritten mid-typing is worse than a beat of loading. If the
  // product fetch fails (e.g. a hard-deleted variant), fall through to the
  // empty form so ops can re-pick the product.
  const frame = embedded ? { p: 0 } : { borderWidth: '2px', borderColor: 'red.300', rounded: 'md', p: '3' };

  if (item && !seeded && !editProductError) {
    return (
      <Box {...frame} p={embedded ? 0 : '8'} bg='white'>
        <Flex justify='center'>
          <Spinner />
        </Flex>
      </Box>
    );
  }

  return (
    <Box {...frame} bg='white'>
      <SectionTitle label='Product' hint='What is the restaurant currently buying?' />
      {showPicker ? (
        <Box>
          <VariantEliminationFields
            familyId={draft.familyId}
            familyName={draft.familyName}
            masterProductId={draft.masterProductId}
            justCreatedVariantId={justCreatedVariantId}
            excludeIds={excludeIds}
            keepVariantId={item?.masterProductId}
            onPatch={onEliminationPatch}
            onCreateFamily={(typed) => {
              setTypedFamilyName(typed);
              setInlineForm('family');
            }}
            onCreateVariant={() => setInlineForm('variant')}
          />
          {inlineForm === 'family' && (
            <Box mt='2'>
              <InlineFamilyForm
                initialName={typedFamilyName}
                onCancel={() => setInlineForm(null)}
                onCreated={(family) => {
                  patchDraft({
                    familyId: family.id,
                    familyName: family.universalName,
                    sel: {},
                    masterProductId: undefined,
                  });
                  // A brand-new family has zero variants — flow straight into
                  // variant creation (spec decision).
                  setInlineForm('variant');
                }}
              />
            </Box>
          )}
          {inlineForm === 'variant' && draft.familyId && (
            <Box mt='2'>
              <InlineVariantForm
                // Switching family must reset the whole form (all fields are seeded from
                // initialValues once) rather than reuse family A's state against family B.
                key={draft.familyId}
                familyId={draft.familyId}
                familyName={draft.familyName}
                initialValues={draft.sel}
                onCancel={() => setInlineForm(null)}
                onCreated={(product) => {
                  // Seed the picks from the created variant; the invalidated
                  // variants query refetches and the elimination resolves to it.
                  const sel: AttrSelections = {};
                  ELIMINATION_ATTRS.forEach((k) => {
                    sel[k] = attrValue(product, k);
                  });
                  patchDraft({ sel, masterProductId: product.id });
                  setJustCreatedVariantId(product.id);
                  setInlineForm(null);
                  // Same as picking a row: the product is resolved, collapse.
                  setPickerOpen(false);
                }}
              />
            </Box>
          )}
        </Box>
      ) : (
        // Collapsed summary strip: what was picked, at a glance. Built from
        // the draft picks so it renders even while the variants query is
        // refetching (e.g. right after inline creation).
        <Flex align='center' gap='3' borderWidth='1px' borderColor='red.200' bg='red.50' rounded='md' px='3' py='2'>
          {selectedVariant?.imageUrl ? (
            <Image src={selectedVariant.imageUrl} alt='' boxSize='32px' objectFit='cover' rounded='sm' />
          ) : (
            <Box color='gray.400' fontSize='20px' px='1'>
              <FiImage />
            </Box>
          )}
          <Box flex='1' minW='0'>
            <Text fontSize='sm' fontWeight='semibold' noOfLines={1}>
              {draft.familyName}
              {stripAttrs && (
                <Box as='span' fontWeight='normal' color='gray.700'>
                  {` — ${stripAttrs}`}
                </Box>
              )}
            </Text>
            {stripUnits && (
              <Text fontSize='xs' color='gray.600' noOfLines={1}>
                {stripUnits}
              </Text>
            )}
          </Box>
          <Button size='xs' variant='outline' flexShrink={0} onClick={() => setPickerOpen(true)}>
            Change
          </Button>
        </Flex>
      )}

      {locked && (
        <Text fontSize='xs' color='gray.500' mt='2'>
          Pick a variant above to unlock flexibility and demand.
        </Text>
      )}

      <Box mt='5' opacity={locked ? 0.45 : undefined} pointerEvents={locked ? 'none' : undefined}>
        <SectionTitle label='Flexibility' hint='What is the restaurant willing to change?' />
        <FlexibilityFields draft={draft} activeVariants={activeVariants} locked={locked} onPatch={patchDraft} />

        <Box mt='5'>
          <SectionTitle label='Demand' />
          <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap='3'>
            <FormControl isRequired>
              <FormLabel fontSize='sm'>Volume (per order)</FormLabel>
              <Input
                size='sm'
                placeholder='e.g. 100'
                value={draft.volume}
                isDisabled={locked}
                onChange={(e) => patchDraft({ volume: numOnly(e.target.value) })}
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize='sm'>Current price (AED)</FormLabel>
              <Input
                size='sm'
                placeholder='AED'
                value={draft.currentPrice}
                isDisabled={locked}
                onChange={(e) => patchDraft({ currentPrice: numOnly(e.target.value) })}
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize='sm'>Target price (AED)</FormLabel>
              <Input
                size='sm'
                placeholder='AED'
                value={draft.targetPrice}
                isDisabled={locked}
                onChange={(e) => patchDraft({ targetPrice: numOnly(e.target.value) })}
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize='sm'>Item expiration date</FormLabel>
              <Input
                size='sm'
                type='date'
                value={draft.expirationDate}
                isDisabled={locked}
                onChange={(e) => patchDraft({ expirationDate: e.target.value })}
              />
            </FormControl>
            <FormControl display='flex' alignItems='center' h='100%' pt={{ md: '7' }}>
              <Checkbox
                size='sm'
                isChecked={draft.nearExpiry}
                isDisabled={locked}
                onChange={(e) => patchDraft({ nearExpiry: e.target.checked })}>
                Item is near expiry
              </Checkbox>
            </FormControl>
          </Grid>
        </Box>
      </Box>

      <Flex justify='space-between' mt='5'>
        <Button size='sm' variant='ghost' onClick={onClose} isDisabled={isSaving}>
          Cancel
        </Button>
        <Button
          size='sm'
          colorScheme='red'
          onClick={save}
          isLoading={isSaving}
          loadingText='Saving…'
          isDisabled={!draftValid(draft)}>
          {item ? 'Save changes' : 'Save item'}
        </Button>
      </Flex>
    </Box>
  );
};
